'use client'
import clsx from 'clsx'
import { LazyMotion, m } from 'framer-motion'
import Link from 'next/link'
import { type JSX, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ElementPopper from 'react-element-popper'

import { useWindowSize } from '../../hooks/useWindowSize'
import Icon from '../Icon'
import LoadingText from '../LoadingText'

const motionFeatures = () => import('ui/helpers/motionFeatures').then(res => res.default)

type SelectProps<T> = {
  options: Array<{ value: T; title: string | number; url?: string; hexColor?: string | null }>
  onSelect?: (value: T) => void
  value?: T
  placeholder?: string
  selectProps?: Omit<JSX.IntrinsicElements['button'], 'className'>
  asLink?: boolean
  className?: string
  selectClassName?: string
  optionProps?: Omit<JSX.IntrinsicElements['button'], 'className'>
  onInit?: (value: T) => void
  loading?: boolean
}

export default function Select<T>({
  options,
  onSelect,
  onInit,
  value,
  placeholder,
  selectProps,
  asLink,
  className,
  selectClassName,
  optionProps,
  loading,
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false)
  const [isCloseAnimationStarted, setIsCloseAnimationStarted] = useState(false)
  const [isDropdownTop, setIsDropdownTop] = useState(false)
  const [dropdownWidth, setDropdownWidth] = useState(0)

  const { windowSize } = useWindowSize()

  const selectedOptionIndex = useMemo(
    () => options.findIndex(option => option.value === value),
    [options, value],
  )

  const selectRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const optionsRef = useRef<Array<HTMLButtonElement | null>>([])

  const handleOutsideClick = useCallback((e: MouseEvent) => {
    if (e.target instanceof Node && !menuRef.current?.contains(e.target)) {
      setIsCloseAnimationStarted(true)
    }
  }, [])

  const openDropdown = useCallback(() => {
    document.addEventListener('click', handleOutsideClick)
    setIsOpen(true)
    requestAnimationFrame(() => {
      optionsRef.current[selectedOptionIndex]?.focus()
    })
  }, [handleOutsideClick, selectedOptionIndex])

  const closeDropdown = useCallback(() => {
    setIsCloseAnimationStarted(true)
  }, [])

  const onCloseAnimationEnd = useCallback(() => {
    if (isCloseAnimationStarted) {
      setIsOpen(false)
      setIsCloseAnimationStarted(false)
      document.removeEventListener('click', handleOutsideClick)
      selectRef.current?.focus()
    }
  }, [handleOutsideClick, isCloseAnimationStarted])

  const handleOptionClick = useCallback(
    (itemValue: T) => {
      closeDropdown()
      onSelect?.(itemValue)
    },
    [closeDropdown, onSelect],
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' && !isOpen && selectedOptionIndex !== options.length - 1) {
      event.preventDefault()
      onSelect?.(options[selectedOptionIndex + 1].value)
    } else if (event.key === 'ArrowUp' && !isOpen && selectedOptionIndex !== 0) {
      event.preventDefault()
      onSelect?.(options[selectedOptionIndex - 1].value)
    }
  }

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()

        if (isDropdownTop) {
          if (index === 0) break

          optionsRef.current[index - 1]?.focus()
        } else {
          if (index === options.length - 1) break

          optionsRef.current[index + 1]?.focus()
        }

        break
      }

      case 'ArrowUp': {
        event.preventDefault()

        if (isDropdownTop) {
          if (index === options.length - 1) break

          optionsRef.current[index + 1]?.focus()
        } else {
          if (index === 0) break

          optionsRef.current[index - 1]?.focus()
        }

        break
      }

      case 'Tab':
      case 'Escape':
      case 'Enter': {
        if (event.key === 'Enter' && asLink) return
        event.preventDefault()

        closeDropdown()
        selectRef.current?.focus()
      }
    }
  }

  useLayoutEffect(() => {
    if (!selectRef.current) return

    setDropdownWidth(selectRef.current.getBoundingClientRect().width)

    return () => document.removeEventListener('click', handleOutsideClick)
  }, [handleOutsideClick, windowSize])

  useEffect(() => {
    if (onInit && value) onInit(value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={isOpen && isDropdownTop ? 'mt-[1px]' : 'mb-[1px]'}>
      <LazyMotion features={motionFeatures} strict>
        <ElementPopper
          ref={menuRef}
          zIndex={100}
          containerClassName={className}
          element={
            <button
              className={clsx(
                'radius-sm flex items-center justify-between border border-black bg-white px-5 py-3 lg:rounded-sm xl:px-6 xl:py-5',
                isOpen &&
                  (isDropdownTop ? '!rounded-t-none !border-t-0' : '!rounded-b-none !border-b-0'),
                selectClassName,
              )}
              onClick={() => {
                if (isOpen) {
                  closeDropdown()
                } else {
                  openDropdown()
                }
              }}
              onKeyDown={handleKeyDown}
              //@ts-ignore
              ref={selectRef}
              {...selectProps}
            >
              {options[selectedOptionIndex]?.hexColor && (
                <div
                  className='radius-pill h-4 w-4 xl:h-5 xl:w-5'
                  style={{ backgroundColor: options[selectedOptionIndex].hexColor ?? undefined }}
                />
              )}
              <h6
                className={clsx(
                  'h6',
                  options[selectedOptionIndex]?.hexColor && 'ml-3 w-full text-left',
                )}
                data-testid='select-value'
              >
                {loading ? (
                  <LoadingText textLength={15} />
                ) : (
                  (options[selectedOptionIndex]?.title ?? placeholder)
                )}
              </h6>

              <div className='flex flex-col'>
                <Icon name='MdExpandLess' size={16} />

                <Icon name='MdExpandMore' size={16} />
              </div>
            </button>
          }
          popper={
            <m.ul
              animate={{
                opacity: 1,
                scaleY: isOpen && !isCloseAnimationStarted ? 1 : 0,
              }}
              className={clsx(
                'radius-sm relative flex border bg-white lg:rounded-sm',
                isDropdownTop
                  ? 'top-px origin-bottom flex-col-reverse !rounded-b-none border-b-0'
                  : 'bottom-px origin-top flex-col !rounded-t-none border-t-0',
              )}
              style={{ width: dropdownWidth }}
              onAnimationComplete={onCloseAnimationEnd}
            >
              {options.map(({ value: itemValue, title, url, hexColor }, index) => (
                <li key={index} className='w-full'>
                  {asLink && url ? (
                    <Link
                      tabIndex={isOpen ? undefined : -1}
                      className='inline-block w-full no-underline'
                      href={url}
                    >
                      <button
                        data-testid='select-option'
                        onClick={() => handleOptionClick(itemValue)}
                        tabIndex={isOpen ? undefined : -1}
                        className={clsx(
                          'flex w-full items-center gap-3 px-5 py-3 transition-colors xl:px-6 xl:py-5',
                          itemValue === value && 'bg-lightgray',
                        )}
                        onKeyDown={event => handleOptionKeyDown(event, index)}
                        //@ts-ignore
                        ref={ref => {
                          optionsRef.current[index] = ref
                        }}
                        onFocus={() => {
                          onSelect?.(itemValue)
                        }}
                        {...optionProps}
                      >
                        {hexColor && (
                          <div
                            className='radius-pill h-4 w-4 xl:h-5 xl:w-5'
                            style={{ backgroundColor: hexColor }}
                          />
                        )}
                        <h6 className='h6 text-left'>{title}</h6>
                      </button>
                    </Link>
                  ) : (
                    <button
                      data-testid='select-option'
                      onClick={() => handleOptionClick(itemValue)}
                      tabIndex={isOpen ? undefined : -1}
                      className={clsx(
                        'w-full px-5 py-3 transition-colors xl:px-6 xl:py-5',
                        itemValue === value && 'bg-lightgray',
                      )}
                      onKeyDown={event => handleOptionKeyDown(event, index)}
                      //@ts-ignore
                      ref={ref => {
                        optionsRef.current[index] = ref
                      }}
                      onFocus={() => {
                        onSelect?.(itemValue)
                      }}
                      {...optionProps}
                    >
                      <h6 className='h6'>{title}</h6>
                    </button>
                  )}
                </li>
              ))}
            </m.ul>
          }
          position='bottom-center'
          active={isOpen}
          onChange={e => {
            setIsDropdownTop(e.position === 'top-center')
          }}
        />
      </LazyMotion>
    </div>
  )
}
